/**
 * iter 34 Phase F — Customer Support page.
 *
 * Role-aware ticket inbox:
 *   - Normal users: see their own org's tickets + can create new ones.
 *   - Developer users: see ALL orgs (with an org filter), can assign,
 *     change status, post internal messages.
 *
 * Two-pane layout: left = filterable list, right = detail / messaging.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Drawer } from "../components/UI";
import {
  Lifebuoy, Plus, Paperclip, ChatCircleDots, ShieldCheck,
  Funnel, Trash, FileText, ArrowRight,
} from "@phosphor-icons/react";

const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex items-center justify-between mb-4">
    <div>
      <h1 className="text-xl font-medium" data-testid="page-title">{title}</h1>
      {subtitle && <div className="text-[10px] mono text-muted-foreground uppercase">{subtitle}</div>}
    </div>
    <div className="flex items-center gap-2">{actions}</div>
  </div>
);

const PRIORITIES = ["", "low", "medium", "high", "urgent"];

export default function CustomerSupport() {
  const { user } = useAuth();
  const isDev = !!user?.is_developer_user || user?.role === "developer";
  const [tickets, setTickets] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [types, setTypes] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [err, setErr] = useState("");

  // Filters
  const [fStatus, setFStatus] = useState("");
  const [fType, setFType] = useState("");
  const [fOrg, setFOrg] = useState("");
  const [fAssigned, setFAssigned] = useState("");

  const loadTickets = () => {
    const qs = new URLSearchParams();
    if (fStatus) qs.set("status", fStatus);
    if (fType) qs.set("type", fType);
    if (isDev && fOrg) qs.set("org", fOrg);
    if (isDev && fAssigned) qs.set("assigned_to", fAssigned);
    api.get(`/support/tickets?${qs}`)
      .then(r => setTickets(r.data))
      .catch(e => setErr(formatErr(e)));
  };

  useEffect(() => {
    api.get("/support/statuses").then(r => setStatuses(r.data)).catch(() => {});
    api.get("/support/types").then(r => setTypes(r.data)).catch(() => {});
    if (isDev) {
      api.get("/support/orgs").then(r => setOrgs(r.data)).catch(() => {});
      api.get("/support/assignable-users").then(r => setUsers(r.data)).catch(() => {});
    }
  }, [isDev]);
  useEffect(() => { loadTickets(); /* eslint-disable-next-line */ }, [fStatus, fType, fOrg, fAssigned, isDev]);

  const selected = useMemo(() => tickets.find(t => t.id === selectedId), [tickets, selectedId]);

  return (
    <div className="p-6 max-w-screen-2xl mx-auto" data-testid="customer-support-page">
      <PageHeader
        title="Customer Support"
        subtitle={isDev ? "DEVELOPER / TICKET QUEUE" : "HELP / MY TICKETS"}
        actions={
          <button className="btn btn-primary" onClick={() => setOpenNew(true)} data-testid="support-new-ticket-btn">
            <Plus size={14} /> NEW TICKET
          </button>
        }
      />

      {err && <div className="text-xs text-destructive mb-3" data-testid="support-error">{err}</div>}

      {/* Filters */}
      <div className="surface p-3 mb-4 flex flex-wrap gap-3 items-center" data-testid="support-filters">
        <span className="label-xs flex items-center gap-1"><Funnel size={12} /> FILTERS</span>
        <select className="input" style={{ maxWidth: 180 }} value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="support-filter-status">
          <option value="">All statuses</option>
          {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select className="input" style={{ maxWidth: 180 }} value={fType} onChange={(e) => setFType(e.target.value)} data-testid="support-filter-type">
          <option value="">All types</option>
          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {isDev && (
          <>
            <select className="input" style={{ maxWidth: 220 }} value={fOrg} onChange={(e) => setFOrg(e.target.value)} data-testid="support-filter-org">
              <option value="">All organizations</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <select className="input" style={{ maxWidth: 220 }} value={fAssigned} onChange={(e) => setFAssigned(e.target.value)} data-testid="support-filter-assigned">
              <option value="">Anyone assigned</option>
              {users.map(usr => <option key={usr.id} value={usr.id}>{usr.name || usr.email}</option>)}
            </select>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* List */}
        <div className="lg:col-span-2 surface" data-testid="support-list">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <span className="heading">Tickets</span>
            <span className="text-xs text-muted-foreground">{tickets.length}</span>
          </div>
          {tickets.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground" data-testid="support-list-empty">
              No tickets yet. Click NEW TICKET to submit one.
            </div>
          ) : (
            <div className="divide-y divide-border max-h-[70vh] overflow-y-auto">
              {tickets.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedId(t.id)}
                  className={`w-full text-left p-3 hover:bg-muted/30 ${selectedId === t.id ? "bg-muted/40" : ""}`}
                  data-testid={`support-ticket-row-${t.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm truncate flex-1">{t.title}</span>
                    {t.priority && <PriorityChip p={t.priority} />}
                  </div>
                  <div className="flex flex-wrap gap-2 text-[10px] mono uppercase">
                    <span className="badge badge-grey">{t.type_name}</span>
                    <StatusBadge name={t.status_name} />
                    {isDev && t.org_name && <span className="text-muted-foreground">{t.org_name}</span>}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1 mono">
                    {t.user_reported_name} · {t.created_at ? new Date(t.created_at).toLocaleString() : ""}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-3" data-testid="support-detail">
          {!selected ? (
            <div className="surface p-8 text-center text-muted-foreground text-sm" data-testid="support-detail-empty">
              <Lifebuoy size={36} className="mx-auto mb-2 opacity-50" />
              Select a ticket to view details.
            </div>
          ) : (
            <TicketDetail
              ticketId={selected.id}
              isDev={isDev}
              statuses={statuses}
              types={types}
              users={users}
              onChanged={loadTickets}
            />
          )}
        </div>
      </div>

      <NewTicketDrawer
        open={openNew}
        types={types}
        onClose={() => setOpenNew(false)}
        onCreated={(t) => { setOpenNew(false); loadTickets(); setSelectedId(t.id); }}
      />
    </div>
  );
}


function PriorityChip({ p }) {
  const cls = p === "urgent" || p === "high" ? "text-destructive" : p === "medium" ? "text-amber-600" : "text-muted-foreground";
  return <span className={`text-[10px] mono uppercase ${cls}`} title="priority">{p}</span>;
}

function StatusBadge({ name }) {
  const cls = (name || "").toLowerCase().includes("fixed and confirmed") ? "badge-green"
    : (name || "").toLowerCase().includes("waiting on confirmation") ? "badge-blue"
    : (name || "").toLowerCase().includes("investigating") ? "badge-amber" : "badge-grey";
  return <span className={`badge ${cls}`} data-testid={`support-status-badge-${(name || "").replace(/\s+/g, "-")}`}>{name || "—"}</span>;
}


function NewTicketDrawer({ open, types, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", support_ticket_type: "", priority: "" });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ title: "", description: "", support_ticket_type: types[0]?.id || "", priority: "" });
      setFile(null);
      setErr("");
    }
  }, [open, types]);

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.support_ticket_type) {
      setErr("Title, description, and type are required.");
      return;
    }
    setSubmitting(true); setErr("");
    try {
      const r = await api.post("/support/tickets", form);
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        try { await api.post(`/support/tickets/${r.data.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (ex) { console.warn("File upload failed:", formatErr(ex)); }
      }
      if (onCreated) onCreated(r.data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New Support Ticket"
      testid="support-new-drawer"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose} disabled={submitting} data-testid="support-new-cancel">CANCEL</button>
        <button className="btn btn-primary" onClick={submit} disabled={submitting} data-testid="support-new-submit">
          {submitting ? "SUBMITTING…" : "SUBMIT"}
        </button>
      </>}
    >
      <div className="space-y-3" data-testid="support-new-form">
        <div>
          <div className="label-xs mb-1">TITLE</div>
          <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="support-new-title" />
        </div>
        <div>
          <div className="label-xs mb-1">TYPE</div>
          <select className="input" value={form.support_ticket_type} onChange={(e) => setForm({ ...form, support_ticket_type: e.target.value })} data-testid="support-new-type">
            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <div className="label-xs mb-1">PRIORITY (OPTIONAL)</div>
          <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="support-new-priority">
            {PRIORITIES.map(p => <option key={p} value={p}>{p || "(unset)"}</option>)}
          </select>
        </div>
        <div>
          <div className="label-xs mb-1">DESCRIPTION</div>
          <textarea className="input" rows={8} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="support-new-description" />
        </div>
        <div>
          <div className="label-xs mb-1">SCREENSHOT / FILE (OPTIONAL)</div>
          <input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="support-new-file" />
          {file && <div className="text-[10px] mono mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
        </div>
        {err && <div className="text-xs text-destructive" data-testid="support-new-error">{err}</div>}
      </div>
    </Drawer>
  );
}


function TicketDetail({ ticketId, isDev, statuses, types, users, onChanged }) {
  const [t, setT] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [files, setFiles] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    api.get(`/support/tickets/${ticketId}`).then(r => setT(r.data)).catch(e => setErr(formatErr(e)));
    api.get(`/support/tickets/${ticketId}/messages`).then(r => setMsgs(r.data)).catch(() => {});
    api.get(`/support/tickets/${ticketId}/files`).then(r => setFiles(r.data)).catch(() => {});
  };
  useEffect(() => { if (ticketId) load(); /* eslint-disable-next-line */ }, [ticketId]);

  const postMsg = async () => {
    if (!newMsg.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/support/tickets/${ticketId}/messages`, { message_body: newMsg.trim(), is_internal: isInternal });
      setNewMsg("");
      load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const uploadFile = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/support/tickets/${ticketId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  const changeStatus = async (id) => {
    try { await api.patch(`/support/tickets/${ticketId}`, { support_ticket_status: id }); load(); if (onChanged) onChanged(); }
    catch (e) { setErr(formatErr(e)); }
  };

  const changeAssigned = async (uid) => {
    try { await api.patch(`/support/tickets/${ticketId}`, { assigned_to: uid || null }); load(); if (onChanged) onChanged(); }
    catch (e) { setErr(formatErr(e)); }
  };

  if (!t) return <div className="surface p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="surface p-4">
        <div className="flex items-start gap-2 mb-1">
          <h2 className="text-lg font-medium flex-1" data-testid="support-detail-title">{t.title}</h2>
          <StatusBadge name={t.status_name} />
        </div>
        <div className="flex flex-wrap gap-2 text-[10px] mono uppercase mb-3">
          <span className="badge badge-grey">{t.type_name}</span>
          {t.priority && <PriorityChip p={t.priority} />}
          <span className="text-muted-foreground">REPORTED BY {t.user_reported_name}</span>
          {isDev && t.org_name && <span className="text-muted-foreground">ORG: {t.org_name}</span>}
          <span className="text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</span>
        </div>
        <div className="text-sm whitespace-pre-wrap" data-testid="support-detail-description">{t.description}</div>

        {isDev && (
          <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2 items-center" data-testid="support-dev-controls">
            <span className="label-xs flex items-center gap-1"><ShieldCheck size={12} /> DEV</span>
            <select className="input" style={{ maxWidth: 220 }} value={t.support_ticket_status} onChange={(e) => changeStatus(e.target.value)} data-testid="support-status-select">
              {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select className="input" style={{ maxWidth: 220 }} value={t.assigned_to || ""} onChange={(e) => changeAssigned(e.target.value)} data-testid="support-assign-select">
              <option value="">— Unassigned —</option>
              {users.map(usr => <option key={usr.id} value={usr.id}>{usr.name || usr.email}</option>)}
            </select>
          </div>
        )}

        {files.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border" data-testid="support-detail-files">
            <div className="label-xs mb-1 flex items-center gap-1"><Paperclip size={12} /> ATTACHMENTS</div>
            <div className="flex flex-wrap gap-2">
              {files.map(f => (
                <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer"
                  className="surface p-2 inline-flex items-center gap-2 hover:bg-muted/30 text-xs"
                  data-testid={`support-file-${f.id}`}>
                  <FileText size={14} />
                  <span className="truncate" style={{ maxWidth: 200 }}>{f.file_name}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="surface" data-testid="support-detail-messages">
        <div className="p-3 border-b border-border flex items-center gap-2">
          <ChatCircleDots size={14} />
          <span className="heading">Messages ({msgs.length})</span>
        </div>
        <div className="p-3 space-y-3 max-h-[40vh] overflow-y-auto">
          {msgs.length === 0 ? (
            <div className="text-sm text-muted-foreground italic" data-testid="support-msg-empty">No messages yet.</div>
          ) : msgs.map(m => (
            <div key={m.id} className={`p-2 rounded ${m.is_internal ? "border border-amber-500 bg-amber-50/40 dark:bg-amber-950/20" : "border border-border"}`} data-testid={`support-msg-${m.id}`}>
              <div className="text-[10px] mono uppercase text-muted-foreground mb-1 flex justify-between">
                <span>
                  {m.created_by_name}
                  {m.is_internal && <span className="ml-2 text-amber-600 font-medium">INTERNAL</span>}
                </span>
                <span>{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span>
              </div>
              <div className="text-sm whitespace-pre-wrap">{m.message_body}</div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-border space-y-2">
          <textarea
            className="input"
            rows={3}
            placeholder="Add a reply…"
            value={newMsg}
            onChange={(e) => setNewMsg(e.target.value)}
            data-testid="support-msg-input"
          />
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2 text-xs">
              {isDev && (
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} data-testid="support-msg-internal" />
                  <span className="mono uppercase">Internal note</span>
                </label>
              )}
              <label className="btn btn-ghost btn-sm cursor-pointer" data-testid="support-msg-attach">
                <Paperclip size={12} /> ATTACH
                <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => uploadFile(e.target.files?.[0])} />
              </label>
            </div>
            <button className="btn btn-primary btn-sm" onClick={postMsg} disabled={busy || !newMsg.trim()} data-testid="support-msg-send">
              <ArrowRight size={12} /> {busy ? "SENDING…" : "SEND"}
            </button>
          </div>
          {err && <div className="text-xs text-destructive" data-testid="support-detail-error">{err}</div>}
        </div>
      </div>
    </div>
  );
}
